import React, { useState, useRef, useCallback, useEffect } from 'react';

export interface ResizableSplitPanelProps {
  leftPanel: React.ReactNode;
  rightPanel: React.ReactNode;
  defaultLeftWidth?: number; // percentage (0-100)
  minLeftWidth?: number; // percentage
  minRightWidth?: number; // percentage
  className?: string;
}

/**
 * A resizable split panel component that divides the screen into left and right sections
 * with an adjustable divider between them.
 *
 * Features:
 * - Drag-and-drop resizing
 * - Minimum width constraints for both panels
 * - Smooth transitions and visual feedback
 * - Accessible and keyboard-friendly
 */
export const ResizableSplitPanel: React.FC<ResizableSplitPanelProps> = ({
  leftPanel,
  rightPanel,
  defaultLeftWidth = 50,
  minLeftWidth = 30,
  minRightWidth = 30,
  className = '',
}) => {
  const [leftWidth, setLeftWidth] = useState(defaultLeftWidth);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const mouseX = e.clientX - containerRect.left;

      // Calculate new left panel width as percentage
      let newLeftWidth = (mouseX / containerWidth) * 100;

      // Apply minimum width constraints
      newLeftWidth = Math.max(minLeftWidth, Math.min(100 - minRightWidth, newLeftWidth));

      setLeftWidth(newLeftWidth);
    },
    [isDragging, minLeftWidth, minRightWidth]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      // Prevent text selection while dragging
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const rightWidth = 100 - leftWidth;

  return (
    <div
      ref={containerRef}
      className={`flex h-full w-full overflow-hidden relative ${className}`}
    >
      {/* Left Panel - height constrained, no overflow here */}
      <div
        className="h-full flex-shrink-0 overflow-hidden"
        style={{ width: `${leftWidth}%` }}
      >
        {leftPanel}
      </div>

      {/* Resizable Divider */}
      <div
        className={`
          relative flex items-center justify-center flex-shrink-0
          w-1 bg-default-200 hover:bg-blue-400 cursor-col-resize
          transition-colors duration-200 select-none
          ${isDragging ? 'bg-blue-500' : ''}
        `}
        onMouseDown={handleMouseDown}
        role="separator"
        aria-orientation="vertical"
        aria-valuenow={leftWidth}
        aria-valuemin={minLeftWidth}
        aria-valuemax={100 - minRightWidth}
        tabIndex={0}
        onKeyDown={(e) => {
          const step = 5;
          if (e.key === 'ArrowLeft') {
            setLeftWidth(prev => Math.max(minLeftWidth, prev - step));
          } else if (e.key === 'ArrowRight') {
            setLeftWidth(prev => Math.min(100 - minRightWidth, prev + step));
          }
        }}
      >
        {/* Drag Handle Visual Indicator */}
        <div className="absolute inset-y-0 flex items-center justify-center w-4 -translate-x-1/2 left-1/2">
          <div className="flex flex-col gap-1">
            <div className="w-1 h-1 rounded-full bg-default-400"></div>
            <div className="w-1 h-1 rounded-full bg-default-400"></div>
            <div className="w-1 h-1 rounded-full bg-default-400"></div>
          </div>
        </div>
      </div>

      {/* Right Panel - height constrained, no overflow here */}
      <div
        className="h-full flex-shrink-0 overflow-hidden"
        style={{ width: `${rightWidth}%` }}
      >
        {rightPanel}
      </div>
    </div>
  );
};

export default ResizableSplitPanel;
